import NavigationSider from '@hellodarwin/app-core/lib/layouts/navigation';
import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import { NavigationLink } from '@hellodarwin/core/lib/features/entities';
import useMediaQuery from '@hellodarwin/core/lib/features/helpers/use-media-query';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Accueil from '@hellodarwin/icons/dist/icons/Accueil';
import ArtificialIntelligence from '@hellodarwin/icons/dist/icons/ArtificialIntelligence';
import DeploymentUnit from '@hellodarwin/icons/dist/icons/DeploymentUnit';
import Grants from '@hellodarwin/icons/dist/icons/Grants';
import Help from '@hellodarwin/icons/dist/icons/Help';
import Language from '@hellodarwin/icons/dist/icons/Language';
import Notifications from '@hellodarwin/icons/dist/icons/Notifications';
import ProjectIcon from '@hellodarwin/icons/dist/icons/ProjectIcon';
import Search from '@hellodarwin/icons/dist/icons/Search';
import User from '@hellodarwin/icons/dist/icons/User';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../app/index';
import { useClientAnalytics } from '../features/analytics/use-client-analytics';
import {
  fetchApplicationNotifications,
  selectApplicationNotificationTotal,
} from '../features/api/slices/client-application-notifications-slice';
import { selectCompanyAdmin } from '../features/api/slices/global-slice';
import { selectProfile } from '../features/api/slices/profile-slice';
import { useNewClientApi } from '../features/api/use-client-api';

const Navigation = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: hdT } = useTranslations();
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.tablet });
  const profile = useAppSelector(selectProfile);
  const accessLevel = usePremiumAccess();
  const admin = useAppSelector(selectCompanyAdmin);
  const notificationTotal = useAppSelector(selectApplicationNotificationTotal);
  const newApi = useNewClientApi();
  const analytics = useClientAnalytics();

  // Product rule: the applications badge is Custom-only. Still load-bearing at render
  // time — the applications page fetches for every tier, so the store can hold counts
  // even when the badge is hidden.
  const showsNotificationBadge = accessLevel.accessLevel === 'custom';

  useEffect(() => {
    if (!showsNotificationBadge) return;
    dispatch(fetchApplicationNotifications({ api: newApi }));
  }, [dispatch, newApi, showsNotificationBadge]);

  const links = useMemo(() => {
    let links: NavigationLink[] = [];
    links = [
      ...(!isTablet
        ? [
            {
              pathname: '/dashboard',
              label: t('navigation.dashboard'),
              Icon: Accueil,
            },
          ]
        : []),
      ...(accessLevel.accessLevel === 'custom'
        ? [
            {
              pathname: '/game-plan',
              label: hdT('navigation|planning'),
              Icon: ProjectIcon,
            },
          ]
        : []),
      {
        pathname: '/funding-explorer',
        label: hdT('navigation|fundingExplorer2'),
        Icon: Search,
      },
      {
        pathname: '/assistant',
        label: hdT('navigation|assistant'),
        Icon: ArtificialIntelligence,
      },
      {
        pathname: '/applications',
        label: hdT('navigation|applications'),
        notifs: showsNotificationBadge ? notificationTotal : 0,
        Icon: Grants,
      },
      {
        pathname: '/funding-intelligence',
        label: hdT('navigation|fundingIntelligence'),
        Icon: Language,
      },
      {
        pathname: '/alerts',
        label: hdT('navigation|alerts'),
        Icon: Notifications,
      },
      {
        pathname: '/ai-integration',
        label: hdT('navigation|aiIntegration'),
        Icon: DeploymentUnit,
      },
      {
        pathname: '/faq',
        label: hdT('navigation|support'),
        Icon: Help,
        subLinks: [
          {
            pathname: '/resources',
            label: t('resource|resources'),
          },
          {
            pathname: '/faq',
            label: hdT('navigation|faq'),
          },
          {
            pathname: '/contact-us',
            label: hdT('navigation|contactUs'),
          },
        ],
      },
    ];
    return links;
  }, [isTablet, t, hdT, notificationTotal, showsNotificationBadge, accessLevel]);

  const userLinks: NavigationLink[] = useMemo(() => {
    return [
      {
        label: t('navigation.preferences'),
        pathname: '/profile',
        Icon: User,
      },
    ];
  }, [t]);

  return (
    <NavigationSider
      links={links}
      app="client"
      userLinks={userLinks}
      profile={profile}
      admin={admin}
      defaultOpen
      openMeetingModalClicked={analytics.openMeetingModalClicked}
    />
  );
};

export default Navigation;
